import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Anchorme } from 'react-anchorme';
import classNames from 'classnames';
import OriginalLanguageButton from '../../shared/OriginalLanguageButton';
import { ServerCommunicationUtil } from '../../../utils/server_communication_util';
import { adjustToSameNominalTimeForUser, monthDayYear } from '@alltrails/shared/utils/timeHelpers';
import LanguageRegionCode from '@alltrails/shared/types/LanguageRegionCode';
import TrailDetailItemForm from '../TrailDetailItemForm';
import TrailDetailAlertsEditor from '../TrailDetailAlertsEditor';
import * as styles from './styles/styles.module.scss';
import { FormattedMessage, useIntl } from '@alltrails/shared/react-intl';

const TrailDetailItem = ({ adminUser, adminOrPendingOwner, descriptionInitiallyExpanded, editItem, loggedInUser, activeTab, trailId, visible }) => {
  const [currentEditItemKey, setCurrentEditItemKey] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submittedPendingEditItem, setSubmittedPendingEditItem] = useState(false);
  const [showInTranslatedLang, setShowInTranslatedLang] = useState(false);
  const [showMoreButton, setShowMoreButton] = useState(false);
  const [submitErrorMessage, setSubmitErrorMessage] = useState('');
  const [item, setItem] = useState(editItem);
  const [truncated, setTruncated] = useState(!descriptionInitiallyExpanded);
  const [seoDescriptionEditItem, setSeoDescriptionEditItem] = useState(editItem.seoDescription);

  const EDIT_ITEM = 'editItem';
  const SEO_DESCRIPTION_EDIT_ITEM = 'seoDescriptionEditItem';
  const DESCRIPTION = 'description';
  const intl = useIntl();

  useEffect(() => {
    checkToShowMoreBtn();
  }, [activeTab, showInTranslatedLang]);

  useEffect(() => {
    setCurrentEditItemKey(false);
  }, [editItem]);

  const checkToShowMoreBtn = () => {
    if (showMoreButton) return;

    window.setTimeout(() => {
      // the text element's width and height evaluate to 0 unless we give the DOM a very small waiting period here
      if (window.elementHasOverflow(`text-container-${editItem.key}`)) {
        setShowMoreButton(true);
      }
    }, 10);
  };

  const clearSubmissionData = () => {
    setCurrentEditItemKey(false);
    setSubmitting(false);
    setSubmitErrorMessage('');
  };

  const submitError = () => {
    setSubmitErrorMessage(intl.formatMessage({ defaultMessage: 'Cannot save your update. Please try again.' }));
    setSubmitting(false);
  };

  const submitSuccess = (data, value) => {
    const updateItem = currentEditItemKey === EDIT_ITEM ? item : seoDescriptionEditItem;
    const updatedItem = {
      ...updateItem,
      sourceText: value,
      value
    };

    clearSubmissionData();
    currentEditItemKey === EDIT_ITEM ? setItem(updatedItem) : setSeoDescriptionEditItem(updatedItem);
  };

  const alertSubmitSuccess = items => {
    const editedItem = {
      ...item,
      items
    };

    clearSubmissionData();
    setItem(editedItem);
  };

  const submitPendingEditItemComplete = () => {
    setCurrentEditItemKey(false);
    setSubmitting(false);
    setSubmittedPendingEditItem(true);
  };

  const adminOrPendingOwnerCallback = (data, itemText) => {
    const url = '/api/alltrails/edits/apply';
    ServerCommunicationUtil.getApiEndpoint(
      url,
      { type: 'trail', target_object_id: trailId },
      data => submitSuccess(data, itemText),
      submitError,
      () => {}
    );
  };

  const postItemCallback = (responseData, itemText) => {
    const url = `/api/alltrails/edits/groups/${responseData.editGroups[0].id}/items`;
    // if user is not admin or pending owner, we do not want to immediately apply this edit, instead leave it as pending
    const editItem = currentEditItemKey === EDIT_ITEM ? item : seoDescriptionEditItem;

    if (adminOrPendingOwner) {
      ServerCommunicationUtil.postApiEndpoint(
        url,
        {
          type: 'trail_detail',
          data: { name: editItem.key, value: itemText }
        },
        data => adminOrPendingOwnerCallback(data, itemText),
        submitError,
        () => {}
      );
    } else {
      ServerCommunicationUtil.postApiEndpoint(
        url,
        {
          type: 'trail_detail',
          data: { name: editItem.key, value: itemText }
        },
        submitPendingEditItemComplete,
        submitError,
        () => {}
      );
    }
  };

  const submit = itemText => {
    setSubmitting(true);
    setSubmitErrorMessage('');

    const url = '/api/alltrails/edits/groups';
    ServerCommunicationUtil.postApiEndpoint(
      url,
      { type: 'trail', target_object_id: trailId },
      data => postItemCallback(data, itemText),
      submitError,
      () => {}
    );
  };

  const submitAlerts = alerts => {
    setSubmitting(true);
    setSubmitErrorMessage('');

    const url = '/api/alltrails/admin/alerts';
    ServerCommunicationUtil.postApiEndpoint(
      url,
      { type: 'trail', id: trailId, alerts },
      data => alertSubmitSuccess(data),
      submitError,
      () => {}
    );
  };

  const getEditCta = key => {
    switch (key) {
      case EDIT_ITEM:
        if (adminOrPendingOwner) {
          return editItem.key === 'alerts' ? (
            <FormattedMessage defaultMessage="Edit trail alerts" />
          ) : (
            <FormattedMessage defaultMessage="Edit description" />
          );
        }
        return <FormattedMessage defaultMessage="Suggest edit" />;
      case SEO_DESCRIPTION_EDIT_ITEM:
        return <FormattedMessage defaultMessage="Edit SEO description (web-only, HTML required)" />;
      default:
        return null;
    }
  };

  const noEditCta = () => {
    if (!loggedInUser) return true;

    if (adminOrPendingOwner) {
      // Cannot edit in-place if not one of our supported languages
      return !!item.unsupportedLanguage;
    }

    return item.key !== DESCRIPTION;
  };

  const getDisplayText = editedItem => {
    let displayText;
    if (showInTranslatedLang && editItem.sourceText) {
      displayText = editedItem.sourceText;
    } else if (editedItem.value) {
      displayText = editedItem.value;
    } else if (editedItem.key === 'alerts') {
      if (adminOrPendingOwner) {
        displayText = editedItem.items
          .map(item => {
            let scheduledStart = '';
            if (item.status === 'S' && item.startDate) {
              const date = adjustToSameNominalTimeForUser(item.startDate);
              scheduledStart = `[STARTS ${monthDayYear(date, LanguageRegionCode.English, false)}]`;
            }
            return `• ${item.content}${item.type === 'AreaAlert' ? ' [PARK]' : ''}${item.alertType === 'C' ? ' [CLOSURE]' : ''}${scheduledStart}`;
          })
          .join('\n');
      } else {
        displayText = editedItem.items.map(item => `• ${item.content}`).join('\n');
      }
    }
    return displayText;
  };

  const renderButton = (label, callback) => {
    return (
      <button className={styles.editButton} type="button" onClick={callback}>
        {label}
      </button>
    );
  };

  const renderEditCTA = item => {
    if (submittedPendingEditItem) {
      return (
        <span>
          <FormatttedMessage defaultMessage="Thank you" />
        </span>
      );
    }

    const cta = getEditCta(EDIT_ITEM);
    const isAlertsForArea = item.key === 'alerts' && !!item.areaSlug;
    const handleEditItemClick = isAlertsForArea
      ? () => window.open(`/admin/bulk_trail_edit/${item.areaSlug}`, '_blank', 'noopener,noreferrer')
      : () => setCurrentEditItemKey(EDIT_ITEM);
    return renderButton(cta, handleEditItemClick);
  };

  const hasSeoDescription = () => {
    return seoDescriptionEditItem && (getDisplayText(seoDescriptionEditItem) || '').length > 0;
  };

  // The SEO description is an HTML-enabled extension of the standard description that is web-only
  const renderSeoDescription = () => {
    if (hasSeoDescription()) {
      const seoDescriptionContents = getDisplayText(seoDescriptionEditItem) || '';
      const seoDescriptionHtml = seoDescriptionContents.replace(/(\r\n|\n|\r)/gm, '');

      return <div className={classNames(styles.displayText, styles.extendedDescription)} dangerouslySetInnerHTML={{ __html: seoDescriptionHtml }} />;
    }
  };

  const renderSeoDescriptionEditCTA = () => {
    if (seoDescriptionEditItem && adminUser) {
      return (
        <p className={styles.editSeoDescriptionCta}>
          {renderButton(<FormattedMessage defaultMessage="Edit SEO description (web-only, HTML required)" />, () =>
            setCurrentEditItemKey(SEO_DESCRIPTION_EDIT_ITEM)
          )}
        </p>
      );
    }
  };

  const renderShowOrigLangBtn = () => {
    const shouldShowOrigLangBtn = item.sourceText && item.sourceText !== item.value;
    if (!shouldShowOrigLangBtn) return null;

    return (
      <div className={styles.languageContainer}>
        <OriginalLanguageButton
          active={showInTranslatedLang}
          handleClick={() => setShowInTranslatedLang(!showInTranslatedLang)}
          eventSource={`TrailDetail ${item.key}`}
        />
      </div>
    );
  };

  const renderShowMoreButton = () => {
    if (!showMoreButton || !truncated) return null;

    return (
      <button className={styles.showMoreBtn} type="button" onClick={() => setTruncated(false)}>
        <FormattedMessage defaultMessage="Show more" />
      </button>
    );
  };

  const renderEditForm = () => {
    const editedItem = currentEditItemKey === EDIT_ITEM ? item : seoDescriptionEditItem;

    return (
      <TrailDetailItemForm
        maxLength={editedItem.maxLength}
        text={editedItem.sourceText ? editedItem.sourceText : editedItem.value}
        handleCancelPress={() => setCurrentEditItemKey(false)}
        handleSubmit={submit}
        errorMessage={submitErrorMessage}
        submitting={submitting}
        label={getEditCta(currentEditItemKey)}
      />
    );
  };

  const renderAlertsList = () => {
    const items = item.items.filter(alert => alert.type == 'TrailAlert');
    const errorMessage = submitErrorMessage || 'Trail-level alerts edited here. Use Bulk Trail Edit for park-level alerts.';
    return (
      <TrailDetailAlertsEditor
        items={items}
        handleCancelPress={() => setCurrentEditItemKey(false)}
        handleSubmit={submitAlerts}
        errorMessage={errorMessage}
        submitting={submitting}
      />
    );
  };

  const renderContentOrForm = () => {
    if (currentEditItemKey) {
      if (item.key === 'alerts') {
        return renderAlertsList();
      }
      return renderEditForm();
    }
    const canEditCTA = !noEditCta();
    return (
      <>
        <div className={styles.textAndBtnContainer}>
          <div
            id={`text-container-${editItem.key}`}
            className={`${styles.displayText} ${truncated && !hasSeoDescription() ? `${styles.lineClamp}` : ''}`}
          >
            <Anchorme target="_blank" rel="noreferrer noopener">
              {getDisplayText(item)}
            </Anchorme>
          </div>
          {renderShowMoreButton()}
        </div>
        {renderSeoDescription()}
        <div className={classNames({ [styles.rowReverse]: !canEditCTA && showMoreButton })}>
          {canEditCTA && renderEditCTA(item)}
          {renderSeoDescriptionEditCTA()}
        </div>
        {renderShowOrigLangBtn()}
      </>
    );
  };

  return <div className={`${styles.trailDetailItemContainer} ${visible ? '' : `${styles.hidden}`}`}>{renderContentOrForm()}</div>;
};

TrailDetailItem.propTypes = {
  trailId: PropTypes.number.isRequired,
  editItem: PropTypes.shape({
    key: PropTypes.string.isRequired
  }).isRequired,
  activeTab: PropTypes.string,
  adminOrPendingOwner: PropTypes.bool,
  adminuser: PropTypes.bool,
  loggedInUser: PropTypes.bool,
  visible: PropTypes.bool,
  descriptionInitiallyExpanded: PropTypes.bool
};

TrailDetailItem.defaultProps = {
  adminOrPendingOwner: false,
  activeTab: '',
  adminUser: false,
  loggedInUser: null,
  visible: false,
  descriptionInitiallyExpanded: false
};

export default TrailDetailItem;
